import { api } from "./api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

// Returns true if the browser currently has an active push subscription.
export async function isPushEnabled() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

// Platform-aware capability detection. Distinguishes "install PWA to enable" (iOS Safari
// tab) from truly unsupported, without relying on userAgent alone as the source of truth.
export function getPushSupport() {
  if (typeof window === "undefined") return { supported: false, reason: "unsupported" };
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  const isStandalone = !!mq || window.navigator.standalone === true;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
  const hasAPIs = "Notification" in window && "serviceWorker" in navigator && "PushManager" in window;
  const secure = window.isSecureContext;
  const permission = "Notification" in window ? Notification.permission : "default";
  let supported = false, reason = "unsupported";
  if (!secure) reason = "insecure";
  else if (hasAPIs) { supported = true; reason = "ok"; }
  else if (isIOS && !isStandalone) reason = "ios-needs-install"; // Web Push only in Home Screen app on iOS
  return { supported, reason, isIOS, isStandalone, permission };
}

// Unsubscribes from push and tells the backend to drop the subscription.
export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const ep = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
      await api.post("/push/unsubscribe", { endpoint: ep }).catch(() => {});
    }
    return true;
  } catch {
    return false;
  }
}
// Must be called from a user gesture for the permission prompt to be reliable.
export async function enablePush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return false;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return false;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/push/vapid-public-key");
      if (!data.publicKey) return false;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
    }
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
    return true;
  } catch {
    return false;
  }
}
